import { isPhysicalProduct } from '@/lib/product-utils';
import { isSiteOutOfStock } from '@/lib/inventory-utils';
import type { ShopperProducts } from '@/scapi';

export const RECENTLY_VIEWED_COOKIE_NAME = 'recently-viewed';
export const RECENTLY_VIEWED_MAX_ITEMS = 10;

export type BasketLineItemLike = {
    productId?: string;
    bundledProductItems?: BasketLineItemLike[];
};

type RecentlyViewedProductLike = {
    id?: string;
    type?: { master?: boolean };
    variants?: Array<{ productId?: string }>;
};

export function mergeRecentlyViewedIds(
    existing: string[],
    productId: string,
    limit = RECENTLY_VIEWED_MAX_ITEMS
): string[] {
    return [...new Set([productId, ...existing])].slice(0, limit);
}

export function parseRecentlyViewedIds(raw: unknown): string[] {
    if (!Array.isArray(raw)) return [];
    const ids = raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
    return [...new Set(ids)].slice(0, RECENTLY_VIEWED_MAX_ITEMS);
}

export function collectBasketProductIds(productItems: BasketLineItemLike[] | null | undefined): Set<string> {
    const ids = new Set<string>();
    for (const item of productItems ?? []) {
        if (item.productId) {
            ids.add(item.productId);
        }
        for (const child of item.bundledProductItems ?? []) {
            if (child.productId) {
                ids.add(child.productId);
            }
        }
    }
    return ids;
}

export function excludeProductsAlreadyInCart<T extends RecentlyViewedProductLike>(
    products: T[],
    cartProductIds: Iterable<string>
): T[] {
    const inCart = cartProductIds instanceof Set ? cartProductIds : new Set(cartProductIds);
    if (inCart.size === 0) {
        return products;
    }

    return products.filter((product) => {
        if (product.id && inCart.has(product.id)) {
            return false;
        }
        if (
            product.type?.master &&
            product.variants?.some((variant) => variant.productId && inCart.has(variant.productId))
        ) {
            return false;
        }
        return true;
    });
}

export function isEligibleRecentlyViewedProduct(product: ShopperProducts.schemas['Product']): boolean {
    return isPhysicalProduct(product) && !isSiteOutOfStock(product);
}

export function filterRecentlyViewedProducts<T extends ShopperProducts.schemas['Product']>(products: T[]): T[] {
    return products.filter(isEligibleRecentlyViewedProduct);
}
